import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Layout } from "../components/Layout";
import { ShareBar } from "../components/ShareBar";
import { fetchDate, fetchRelated } from "../lib/api";
import { CATEGORY_LABELS, MONTH_NAMES, TYPE_LABELS } from "../lib/format";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { useJsonLd } from "../lib/useJsonLd";
import { dateEntryJsonLd } from "../lib/jsonLd";

export const DateDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([fetchDate(id), fetchRelated(id, 6)])
      .then(([d, r]) => {
        if (!alive) return;
        setEntry(d);
        setRelated(r);
      })
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 404) setError("This entry could not be found.");
        else setError(e?.message || "Could not load this entry.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  useDocumentMeta({
    title: entry ? `${entry.title} — Will Not Be Forgotten` : "Will Not Be Forgotten",
    description: entry ? entry.short_description : undefined,
    canonical: `https://willnotbeforgotten.org/date/${id}`,
    image: entry ? `https://willnotbeforgotten.org/api/og/${id}.png` : undefined,
  });

  const jsonLd = useMemo(() => dateEntryJsonLd(entry), [entry]);
  useJsonLd(jsonLd);

  // Share buttons point to the backend share-redirect endpoint, which serves
  // server-rendered OG metadata (title, description, per-entry image card)
  // for social crawlers that can't run the SPA's JS, and meta-refreshes
  // human visitors back to the canonical /date/{id} page.
  const shareUrl = `https://willnotbeforgotten.org/api/share/${id}`;

  return (
    <Layout headerVariant="solid">
      <article data-testid="date-detail-page" className="shell" style={{ padding: "3rem 1.5rem 5rem", maxWidth: 920 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="back-button"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "none", border: "none", color: "hsl(var(--muted))",
            cursor: "pointer", padding: 0, marginBottom: "1.5rem",
            fontSize: "0.85rem", letterSpacing: "0.06em", textTransform: "uppercase",
          }}
        >
          <ArrowLeft size={14} aria-hidden /> Back to archive
        </button>

        {loading && (
          <div data-testid="detail-loading" style={{
            display: "flex", flexDirection: "column", gap: 18,
          }}>
            <div style={{ height: 22, width: 180, background: "hsl(var(--bg-elev))" }} />
            <div style={{ height: 56, width: "75%", background: "hsl(var(--bg-elev))" }} />
            <div style={{ height: 14, width: "100%", background: "hsl(var(--bg-elev))" }} />
            <div style={{ height: 14, width: "95%", background: "hsl(var(--bg-elev))" }} />
            <div style={{ height: 14, width: "80%", background: "hsl(var(--bg-elev))" }} />
          </div>
        )}

        {error && (
          <div role="alert" data-testid="detail-error" style={{ padding: "2rem", border: "1px solid hsl(var(--line))", background: "hsl(var(--bg-elev))" }}>
            <h1 style={{ fontSize: "1.6rem", marginTop: 0 }}>This entry could not be found.</h1>
            <p>The link you followed may be out of date or the entry may have been removed.</p>
            <Link to="/" className="btn btn--primary" style={{ marginTop: 16, display: "inline-flex" }}>
              Return to the archive
            </Link>
          </div>
        )}

        {entry && !loading && !error && (
          <>
            <header>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: "1rem" }}>
                <span className="tag tag--solid" data-testid="detail-status">{entry.recognition_status}</span>
                {(TYPE_LABELS[entry.type] || entry.type) && (TYPE_LABELS[entry.type] || entry.type) !== entry.recognition_status ? (
                  <span className="tag">{TYPE_LABELS[entry.type] || entry.type}</span>
                ) : null}
                {entry.year_established ? (
                  <span className="tag" style={{ fontFamily: "var(--font-display)" }}>Since {entry.year_established}</span>
                ) : null}
              </div>
              <div
                className="archival-figure"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "hsl(var(--accent))",
                  fontWeight: 500,
                }}
                data-testid="detail-date"
              >
                {entry.month ? `${MONTH_NAMES[entry.month]}${entry.day ? " " + entry.day : ""}` : entry.date_label}
              </div>
              <h1 data-testid="detail-title" style={{ marginTop: 8, marginBottom: "1rem", fontSize: "clamp(2.2rem, 4vw, 3.4rem)", lineHeight: 1.05 }}>
                {entry.title}
              </h1>
              <p style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: "clamp(1.15rem, 1.7vw, 1.45rem)",
                lineHeight: 1.5,
                color: "hsl(var(--ink-soft))",
                margin: "0 0 2rem",
              }}>
                {entry.short_description}
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: "3rem" }}>
                <ShareBar
                  url={shareUrl}
                  title={entry.title}
                  description={entry.short_description}
                />
              </div>
            </header>

            <Section eyebrow="Why it matters">
              <p style={{ fontSize: "1.05rem", lineHeight: 1.75 }} data-testid="detail-significance">
                {entry.significance}
              </p>
            </Section>

            {entry.full_history && (
              <Section eyebrow="Full history">
                <div data-testid="detail-full-history" style={{ fontSize: "1.05rem", lineHeight: 1.8, color: "hsl(var(--ink-soft))" }}>
                  {entry.full_history.split(/\n\n+/).map((para, i) => (
                    <p key={i} style={{ marginTop: i === 0 ? 0 : "1.1rem", marginBottom: 0 }}>
                      {para.trim()}
                    </p>
                  ))}
                </div>
              </Section>
            )}

            {entry.people && entry.people.length > 0 && (
              <Section eyebrow="Key figures">
                <ul style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 1.5rem", padding: 0, margin: 0, listStyle: "none" }}>
                  {entry.people.map((p) => (
                    <li key={p} style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem" }}>{p}</li>
                  ))}
                </ul>
              </Section>
            )}

            {entry.categories && entry.categories.length > 0 && (
              <Section eyebrow="Categories">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {entry.categories.map((c) => (
                    <Link
                      key={c}
                      to={`/category/${c}`}
                      className="tag"
                      data-testid={`detail-category-link-${c}`}
                      style={{ textDecoration: "none", cursor: "pointer" }}
                    >
                      {CATEGORY_LABELS[c] || c}
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {entry.suggested_actions && entry.suggested_actions.length > 0 && (
              <Section eyebrow="Ways to mark the day">
                <ul style={{ paddingLeft: "1.1rem", margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  {entry.suggested_actions.map((a, i) => (
                    <li key={i} style={{ fontSize: "1rem", lineHeight: 1.6 }}>{a}</li>
                  ))}
                </ul>
              </Section>
            )}

            {entry.sources && entry.sources.length > 0 && (
              <Section eyebrow="Sources & further reading">
                <ul style={{ display: "flex", flexDirection: "column", gap: 8, margin: 0, padding: 0, listStyle: "none" }}>
                  {entry.sources.map((s) => (
                    <li key={s}>
                      <a href={s} target="_blank" rel="noopener noreferrer" data-testid="detail-source-link" style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        color: "hsl(var(--accent))", fontSize: "0.95rem",
                      }}>
                        {s.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                        <ExternalLink size={13} aria-hidden />
                      </a>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {related && related.length > 0 && (
              <section style={{ marginTop: "4rem", paddingTop: "3rem", borderTop: "1px solid hsl(var(--line))" }}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "1.25rem",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: "2.5rem",
                    marginBottom: "2.5rem",
                    borderBottom: "1px solid hsl(var(--line))",
                  }}
                >
                  <p
                    style={{
                      fontFamily: "var(--font-display)",
                      fontStyle: "italic",
                      fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)",
                      lineHeight: 1.45,
                      margin: 0,
                      maxWidth: 460,
                      color: "hsl(var(--ink-soft))",
                    }}
                  >
                    If this entry meant something to you, pass it along — that&rsquo;s how a date stays remembered.
                  </p>
                  <ShareBar
                    url={shareUrl}
                    title={entry.title}
                    description={entry.short_description}
                  />
                </div>
                <span className="section__eyebrow">Related entries</span>
                <h2 style={{ marginTop: 0, fontSize: "clamp(1.4rem, 2vw, 2rem)" }}>Continue exploring.</h2>
                <ul data-testid="related-list" style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "1rem",
                  margin: "1.5rem 0 0",
                  padding: 0,
                  listStyle: "none",
                }}>
                  {related.map((r) => (
                    <li key={r.id}>
                      <Link to={`/date/${r.id}`} data-testid="related-link" style={{
                        display: "flex", flexDirection: "column", gap: 8,
                        padding: "1.1rem",
                        background: "hsl(var(--bg-elev))",
                        border: "1px solid hsl(var(--line))",
                        textDecoration: "none",
                        transition: "border-color 160ms ease",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "hsl(var(--accent))"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "hsl(var(--line))"; }}
                      >
                        <span className="archival-figure" style={{
                          fontFamily: "var(--font-display)", fontSize: "0.8rem",
                          letterSpacing: "0.12em", textTransform: "uppercase",
                          color: "hsl(var(--accent))",
                        }}>
                          {r.month ? `${MONTH_NAMES[r.month]}${r.day ? " " + r.day : ""}` : r.date_label}
                        </span>
                        <span style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", color: "hsl(var(--ink))", fontWeight: 500 }}>
                          {r.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </article>
    </Layout>
  );
};

const Section = ({ eyebrow, children }) => (
  <section style={{ marginBottom: "2.25rem" }}>
    <h3 style={{
      fontSize: "0.78rem",
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: "hsl(var(--muted))",
      fontFamily: "var(--font-body)",
      fontWeight: 600,
      marginTop: 0,
      marginBottom: "0.85rem",
      paddingBottom: "0.5rem",
      borderBottom: "1px solid hsl(var(--line))",
    }}>
      {eyebrow}
    </h3>
    {children}
  </section>
);
